// Centralizovan logout helper — DRY za web-admin / web-erp / bilo koji budući app.
//
// Šta radi:
//  1) POST /api/v3/auth/logout (briše cookie sesiju i refresh token na backend-u)
//  2) invalidateMe() — briše TTL cache /me-a u current tabu
//  3) localStorage broadcast "grozd:logout" — drugi tabovi (sa session.ts
//     storage listenerom) automatski invalidate cache + redirect na auth
//  4) Hard navigacija na auth login (dev/prod auto-detekcija po hostname-u)
//
// Velibor (2026-05-02): "neka bude logout i na erp, pa to nam je glavni sistem"
// Pošto će ERP imati istu logiku, odmah radimo shared helper umjesto 2x copy-paste.

import { invalidateMe } from "./session";

const API_URL =
  (typeof window !== "undefined"
    ? (window as any).__GROZD_API_URL__
    : process.env.NEXT_PUBLIC_API_URL) || (typeof window !== "undefined" && window.location.hostname.endsWith(".dev.grozd.me") ? "https://api.dev.grozd.me" : "https://api.grozd.me");

function defaultLoginUrl(): string {
  if (typeof window === "undefined") return "https://auth.grozd.me/login";
  const isDev = window.location.hostname.endsWith(".dev.grozd.me");
  return isDev
    ? "https://auth.dev.grozd.me/login"
    : "https://auth.grozd.me/login";
}

export interface LogoutOptions {
  /** Override redirect URL. Default: auth.grozd.me/login (dev: auth.dev.grozd.me/login). */
  redirectTo?: string;
  /** Ako true, ne radi window.location.href redirect (npr. testovi). */
  skipRedirect?: boolean;
}

/**
 * Globalni logout. Radi best-effort — mrežna greška ne sprječava lokalni cleanup.
 *
 * Posljedice:
 *  - Cookie sesija obrisana na backend-u (best-effort)
 *  - TTL cache obrisan u current tabu
 *  - Drugi tabovi obavješteni preko `storage` event-a (key="grozd:logout")
 *  - Browser preusmjeren na login (osim ako skipRedirect=true)
 */
export async function logout(opts: LogoutOptions = {}): Promise<void> {
  // 1) Best-effort backend logout — ne blokira cleanup ako padne mreža.
  try {
    await fetch(`${API_URL}/api/v3/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // ignore — i dalje invalidate i redirect
  }

  // 2) Lokalni cache invalidate
  invalidateMe();

  // 3) Cross-tab broadcast (storage event hvata druge tabove). Set+remove odmah
  //    da bismo trigger-ovali storage event ali ne ostavili memory u localStorage.
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem("grozd:logout", String(Date.now()));
      window.localStorage.removeItem("grozd:logout");
    }
  } catch {
    // ignore (Safari private mode, quota errors, ...)
  }

  // 4) Hard redirect na auth login
  if (!opts.skipRedirect && typeof window !== "undefined") {
    window.location.href = opts.redirectTo ?? defaultLoginUrl();
  }
}
